import React, { useEffect, useState } from "react";
import { ChatPanel, newAxiosClient } from "@shadowcue/ui";
import type { IChatMessage } from "@shadowcue/ui";

interface Props {
  conversationId: string;
  authToken: string;
}

const STARTER_PROMPTS = [
  "Summarize this candidate's strengths",
  "What were their salary expectations?",
  "Did they mention a notice period?",
  "Any red flags in this screen?",
];

const ConversationChat = ({ conversationId, authToken }: Props) => {
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const axios = React.useMemo(
    () => newAxiosClient({ headers: { AUTHORIZATION: `Bearer ${authToken}` } }),
    [authToken],
  );

  // Load history once on mount.
  useEffect(() => {
    axios
      .get(`/api/conversations/${conversationId}/chat`)
      .then((res) => {
        const loaded: IChatMessage[] = (
          res.data.messages as { role: "user" | "assistant"; content: string }[]
        ).map((m) => ({ role: m.role, text: m.content }));
        setMessages(loaded);
      })
      .catch(() => {
        // History unavailable — start fresh, don't block the widget.
      });
  }, [conversationId]);

  const handleSend = async (message: string) => {
    setMessages((prev) => [...prev, { role: "user", text: message }]);
    setLoading(true);
    setError(null);

    try {
      const res = await axios.post(`/api/conversations/${conversationId}/chat`, {
        message,
      });
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: res.data.response },
      ]);
    } catch (err: unknown) {
      const errorMessage =
        err &&
        typeof err === "object" &&
        "response" in err &&
        err.response &&
        typeof err.response === "object" &&
        "data" in err.response &&
        err.response.data &&
        typeof err.response.data === "object" &&
        "error" in err.response.data
          ? String((err.response.data as { error: string }).error)
          : "Something went wrong. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ChatPanel
      messages={messages}
      onSend={handleSend}
      isLoading={loading}
      error={error}
      onDismissError={() => setError(null)}
      starterPrompts={STARTER_PROMPTS}
      title="Ask AI"
      emptyStateText="Ask anything about this interview transcript."
    />
  );
};

export default ConversationChat;
